import ContentService from "@/services/ContentService";

/**
 * Processes HTML content from a rich text editor before saving to the database.
 * Finds base64 image blobs, uploads them via ContentService, and replaces
 * them with <img-custom contentId> tags for storage.
 *
 * Also normalizes any previously-uploaded images (with signed URLs) back to
 * <img-custom contentId> format so the DB content stays URL-independent.
 *
 * @param {string} content - HTML content from the editor
 * @param {number} clientId - Client ID for the upload
 * @returns {{ content: string, local: string }} content = DB version, local = preview version with signed URLs
 */
export async function uploadEmbeddedContent(content, clientId) {
  if (!content) return { content, local: content };

  let processed = content;
  const imgArray = [];

  while (processed.indexOf('<img src="data:') !== -1) {
    const index = processed.indexOf('<img src="data:');
    const endIndex = processed.indexOf(">", index + 1);
    const imgTag = processed.substring(index, endIndex + 1);
    const imgData = imgTag.substring(imgTag.indexOf('="') + 2, imgTag.length - 2);

    const fileExtension = imgData.substring(imgData.indexOf(":image/") + 7, imgData.indexOf(";"));

    const arr = imgData.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const imgFile = new File([u8arr], "learning_card_image." + fileExtension, {
      type: mime,
    });

    const randomId = Math.floor(Math.random() * Date.now());
    imgArray.push({ data: imgFile, id: randomId });

    processed = processed.replace(imgTag, `<img-custom ${randomId}>`);
  }

  let contentWithSignedUrl = processed;

  // Normalize any existing signed-URL images back to <img-custom contentId>
  const contentIdRegex = /<img\b[^>]*\bid="contentId-(\d+)"[^>]*>/g;
  processed = processed.replace(contentIdRegex, (_match, contentId) => `<img-custom ${contentId}>`);

  if (imgArray.length) {
    await Promise.all(
      imgArray.map(async (img) => {
        const attachment = {
          name: "Learning Card Content",
          description: "Image used within the content of a learning card.",
          category: "LEARNING_CARD_CONTENT",
          clientId,
          file: img.data,
          generateSignedUrl: true,
          type: "image",
        };
        const imageResponse = await ContentService.createContent(attachment, 2);
        img.contentId = imageResponse.contentId;
        img.contentUrlSigned = imageResponse.contentUrlSigned;
      })
    );

    imgArray.forEach((img) => {
      processed = processed.replace(`<img-custom ${img.id}>`, `<img-custom ${img.contentId}>`);
      contentWithSignedUrl = contentWithSignedUrl.replace(
        `<img-custom ${img.id}>`,
        `<img id="contentId-${img.contentId}" src="${img.contentUrlSigned}">`
      );
    });
  }

  return { content: processed, local: contentWithSignedUrl };
}

/**
 * Processes HTML content loaded from the database for display in the editor.
 * Finds <img-custom contentId> tags, fetches signed URLs, and replaces
 * them with renderable <img> tags.
 *
 * @param {string} content - HTML content from the database
 * @returns {string} HTML with signed image URLs for rendering
 */
export async function loadEmbeddedContent(content) {
  if (!content) return content;

  let processed = content;
  const imageArray = [];

  let imageIndex = processed.indexOf("<img-custom");
  while (imageIndex !== -1) {
    const closingTag = processed.indexOf(">", imageIndex);
    const contentId = processed.substring(imageIndex + 12, closingTag);
    imageArray.push(contentId);
    processed = processed.replace(`<img-custom ${contentId}>`, `<img-temp ${contentId}>`);
    imageIndex = processed.indexOf("<img-custom");
  }

  await Promise.all(
    imageArray.map(async (contentId) => {
      const response = await ContentService.getSignedUrlBasedOnContentId(contentId, 2);
      if (response.url) {
        processed = processed.replace(
          `<img-temp ${contentId}>`,
          `<img id="contentId-${contentId}" src="${response.url}">`
        );
      } else {
        processed = processed.replace(`<img-temp ${contentId}>`, "");
      }
    })
  );

  return processed;
}
